import { CompanyStatements, ScoreBreakdown } from '../installer/WorkFinance';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { contractorApi } from '../../utils/installerApi';
import { useContractorSession } from '../../hooks/useContractorSession';
import { COMPANY_FAMILIES, companyFamily, companyJobKind } from '../../shared/companyJobs';
import { CompanyFollowups } from '../installer/InstallerFindings';
import { DistributionControl, CompanyJobDocuments } from './CompanyJobControls';
import { toFa } from '../../utils/faFormat';
import { InstallerTopbar } from '../installer/InstallerChrome';
import CarrierBadge from '../carrier/CarrierBadge';
import '../../styles/InstallerPages.css';
import '../../styles/ContractorDashboard.css';

const JOB_STATUS = {
  pending_assignment: 'در انتظار تخصیص', assigned: 'تخصیص‌یافته', in_progress: 'در حال انجام',
  completed: 'تکمیل‌شده', canceled: 'لغو‌شده',
};
const DEFECT_STATUS = {
  open: 'باز', in_progress: 'در حال اصلاح', fixed: 'رفع‌شده - منتظر تایید', approved: 'تایید شد', rejected: 'ردشده',
};
const HISTORY_DATES = { created_at: 'ثبت', assigned_at: 'تخصیص', started_at: 'شروع', completed_at: 'تکمیل', appointment_at: 'زمان بازدید' };
const scoreClass = (s) => (s >= 85 ? 'good' : s >= 60 ? 'mid' : 'bad');

// Contractor monitoring dashboard: rolled-up quality score, the roster of
// installers (each with its score, add-by-phone + remove), the roster's jobs, and
// a read-only defect drill-in per job. Self-guards on the field session token.
export default function ContractorDashboardPage() {
  const nav = useNavigate();
  const { token, logout } = useContractorSession();
  const activeToken = useRef(token);
  const [me, setMe] = useState(null);
  const [score, setScore] = useState(null);
  const [installers, setInstallers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [addPhone, setAddPhone] = useState('');
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openJob, setOpenJob] = useState(null); // jobId whose defects are shown
  const [jobDefects, setJobDefects] = useState({}); // jobId -> defects[]

  const load = useCallback(async () => {
    if (!token || activeToken.current !== token) return;
    try {
      const [s, ins, jb] = await Promise.all([
        contractorApi.score(token), contractorApi.installers(token), contractorApi.jobs(token),
      ]);
      if (activeToken.current !== token) return;
      setScore(s); setInstallers(ins.installers || []); setJobs(jb.jobs || []); setErr(null);
    } catch (e) { if (activeToken.current === token) setErr(e.message); }
  }, [token]);

  useEffect(() => {
    activeToken.current = token;
    setMe(null); setScore(null); setInstallers([]); setJobs([]); setOpenJob(null); setJobDefects({});
    if (!token) { nav('/contractor/login'); return; }
    let cancelled = false;
    contractorApi.me(token).then((m) => {
      if (cancelled) return;
      if (m.status !== 'approved') { nav('/contractor/pending'); return; }
      setMe(m); load();
    }).catch(() => { if (!cancelled) nav('/contractor/login'); });
    return () => { cancelled = true; activeToken.current = null; };
  }, [token, nav, load]);

  const addInstaller = async () => {
    if (!addPhone.trim()) return;
    setBusy(true); setErr(null);
    try { await contractorApi.addInstaller(token, addPhone.trim()); setAddPhone(''); await load(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  const removeInstaller = async (id) => {
    if (!window.confirm('این نیروی اجرایی از زیرمجموعهٔ شما حذف شود؟')) return;
    setErr(null);
    try { await contractorApi.removeInstaller(token, id); await load(); } catch (e) { setErr(e.message); }
  };
  const toggleDefects = async (jobId) => {
    if (openJob === jobId) { setOpenJob(null); return; }
    setOpenJob(jobId);
    if (!jobDefects[jobId]) {
      try { const r = await contractorApi.jobDefects(token, jobId); setJobDefects((d) => ({ ...d, [jobId]: r.defects || [] })); }
      catch (e) { setErr(e.message); }
    }
  };

  const takeBack = async (jobId) => {
    setBusy(true); setErr(null);
    try { await contractorApi.takeBack(token, jobId); await load(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  const inbox = jobs.filter((j) => j.work_family === 'civil' && j.contractor_id === me?.id && j.status === 'pending_assignment' && !j.assigned_installer_id);
  const monitoring = jobs.filter((j) => !inbox.includes(j));
  const nameOf = (id) => installers.find((i) => i.id === id)?.full_name || '-';

  return (
    <div className="inst">
      <div className="inst-shell">
        <InstallerTopbar sub={me?.company_name || 'پنل شرکت پیمانکار'}
          action={<button className="inst-btn inst-btn-ghost inst-btn-sm" onClick={() => { logout(); nav('/contractor/login'); }}>خروج</button>} />

        {err && <div role="alert" className="inst-alert">{err}</div>}

        <CompanyStatements key={token} token={token} />
        <ScoreBreakdown score={score} />
        {score && (
          <div className="inst-card cdash-scorecard">
            <div className={`cdash-score ${scoreClass(score.score)}`}>{toFa(score.score)}</div>
            <div className="cdash-score-meta">
              <div className="cdash-score-title">امتیاز کیفیت شرکت</div>
              <div className="inst-empty-hint">میانگین وزنی کارکرد ۱۸۰ روز اخیر (از ۱۰۰). مجموع کسر امتیاز: {toFa(score.total_penalties)}</div>
            </div>
          </div>
        )}

        <div className="inst-card">
          <h2 className="inst-h1" style={{ fontSize: 17 }}>نیروهای اجرایی زیرمجموعه ({toFa(installers.length)})</h2>
          <div className="cdash-addrow">
            <input className="inst-input" inputMode="numeric" value={addPhone} placeholder="شماره موبایل نیروی اجرایی"
              aria-label="شماره موبایل نیروی اجرایی" onChange={(e) => setAddPhone(e.target.value)} />
            <button className="inst-btn inst-btn-sm" disabled={busy || !addPhone.trim()} onClick={addInstaller}>افزودن نیروی اجرایی</button>
          </div>
          {installers.length === 0 ? (
            <p className="inst-empty-hint">هنوز نیروی اجرایی اضافه نکرده‌اید. با شمارهٔ موبایلِ نیروی اجراییِ تاییدشده او را اضافه کنید.</p>
          ) : Object.entries(COMPANY_FAMILIES).map(([family, label]) => <section key={family}>
            <h3>{label}</h3>
            <ul className="cdash-list">
              {installers.filter((i) => (i.work_families || ['last_mile']).includes(family)).map((i) => (
                <li key={i.id} className="cdash-row">
                  <span className="cdash-row-name">{i.full_name}</span>
                  <span className={`cdash-pill ${scoreClass(i.score)}`}>{toFa(i.score)}</span>
                  <button className="inst-btn inst-btn-ghost inst-btn-sm" onClick={() => removeInstaller(i.id)}>حذف</button>
                </li>
              ))}
            </ul>
          </section>)}
        </div>

        <div className="inst-card">
          <h2 className="inst-h1">صندوق کارهای دریافتی ({toFa(inbox.length)})</h2>
          {!inbox.length && <p>کاری در انتظار توزیع نیست.</p>}
          <ul className="cdash-list">{inbox.map((j) => <li key={j.id} className="cdash-job">
            <div className="cdash-job-head"><strong>{j.customer_name || 'کار توسعه'}</strong><bdi>{toFa(j.id.slice(0, 8))}</bdi><CarrierBadge carrier={j.carrier} variant="inst" />
              <span>{companyFamily(j)} {companyJobKind(j) && `· ${companyJobKind(j)}`}</span></div>
            <DistributionControl token={token} job={j} onChanged={load} onError={setErr} />
            <CompanyJobDocuments token={token} jobId={j.id} />
            <CompanyFollowups key={`${token}:${j.id}`} token={token} jobId={j.id} />
          </li>)}</ul>
        </div>

        <div className="inst-card">
          <h2 className="inst-h1" style={{ fontSize: 17 }}>کارهای زیرمجموعه ({toFa(jobs.length)})</h2>
          {jobs.length === 0 ? (
            <p className="inst-empty-hint">کاری برای نیروهای اجرایی شما ثبت نشده است.</p>
          ) : (
            <ul className="cdash-list">
              {monitoring.map((j) => (
                <li key={j.id} className="cdash-job">
                  <div className="cdash-job-head">
                    <span className="cdash-row-name">{j.customer_name || 'کار'}</span>
                    <bdi>{toFa(j.id.slice(0, 8))}</bdi>
                    <CarrierBadge carrier={j.carrier} variant="inst" />
                    {j.access !== 'historical' && <span>{companyFamily(j)} {companyJobKind(j) && `· ${companyJobKind(j)}`}</span>}
                    {j.access === 'historical' && <span>سابقه کار (فقط خواندنی)</span>}
                    <span className="cdash-job-status">{JOB_STATUS[j.status] || j.status}</span>
                    <span className="inst-empty-hint">{nameOf(j.assigned_installer_id)}</span>
                    <button className="inst-btn inst-btn-ghost inst-btn-sm" onClick={() => toggleDefects(j.id)}>
                      {openJob === j.id ? 'بستن' : 'ایرادها'}
                    </button>
                  </div>
                  {j.access === 'historical' && <div className="inst-empty-hint">
                    {Object.entries(HISTORY_DATES).filter(([key]) => j[key]).map(([key, label]) => <p key={key}>
                      {label}: {new Date(j[key]).toLocaleString('fa-IR', { timeZone: 'Asia/Tehran' })}
                    </p>)}
                  </div>}
                  {j.access !== 'historical' && (j.contractor_id === me?.id || j.responsible_contractor_id === me?.id) && <>
                    {j.work_family === 'civil' && j.contractor_id === me?.id && j.status === 'assigned' && installers.some((i) => i.id === j.assigned_installer_id) &&
                      <button className="inst-btn inst-btn-ghost inst-btn-sm" disabled={busy} onClick={() => takeBack(j.id)}>بازپس‌گیری کار</button>}
                    <CompanyJobDocuments token={token} jobId={j.id} />
            <CompanyFollowups key={`${token}:${j.id}`} token={token} jobId={j.id} />
                  </>}
                  {openJob === j.id && (
                    <div className="cdash-defects">
                      {!jobDefects[j.id] ? <span className="inst-empty-hint">در حال بارگذاری...</span>
                        : jobDefects[j.id].length === 0 ? <span className="inst-empty-hint">ایرادی ثبت نشده است.</span>
                        : (
                          <ul className="cdash-deflist">
                            {jobDefects[j.id].map((d) => (
                              <li key={d.id || `${d.severity}:${d.status}`}>
                                <strong>{d.count ? `${toFa(d.count)} ایراد` : d.rule || 'ایراد'}</strong>
                                {d.description && <span> - {d.description}</span>}
                                <em className="inst-empty-hint"> ({DEFECT_STATUS[d.status] || d.status})</em>
                              </li>
                            ))}
                          </ul>
                        )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
